import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import Modal from 'LuxKit/Modal/Modal'
import PriceRowMerchandisePriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandisePriceStack'
import TextLink from 'LuxKit/TextLink'
import ToggleButton from 'LuxKit/ToggleButton'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useMerchandiseColorOptions from 'hooks/Merchandise/useMerchandiseColorOptions'
import useMerchandiseOptionData from 'hooks/Merchandise/useMerchandiseOptionData'
import useMerchandiseCartActions from 'hooks/Merchandise/useMerchandiseCartActions'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import MerchandiseOfferPagePointsPayCalculator from './MerchandiseOfferPagePointsPayCalculator'
import { addBusinessDays, formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Counter from 'LuxKit/Counter'
import { getMerchandiseSearchUrl, getMerchandiseBrandSearchUrl } from 'components/Merchandise/merchandiseUtils'
import MerchandiseOfferSizeChart from 'components/Merchandise/MerchandiseOfferSizeChart'
import MerchandiseMaxQuantityMessage from 'components/Merchandise/MerchandiseMaxQuantityMessage'
import MerchandiseLowStockMessage from 'components/Merchandise/MerchandiseLowStockMessage'
import useMerchandiseBrand from 'hooks/Merchandise/useMerchandiseBrand'
import { MerchandiseColourOption } from 'components/Merchandise/MerchandiseColourOption'
import useVariantSeller from 'hooks/Merchandise/useVariantSeller'

const Details = styled(VerticalSpacer)`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${props => props.theme.layout.topScrollOffset};
  }
`

interface Props {
  quantity: number
  offer: App.MerchandiseOffer
  variant: App.MerchandiseOfferVariant
  onVariantChange?: (variant: App.MerchandiseOfferVariant) => void
  onQuantityChange?: (quantity: number) => void
}

function MerchandiseOfferPageDetails(props: Props) {
  const { offer, variant, onVariantChange, onQuantityChange, quantity } = props
  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const [brand] = useMerchandiseBrand(offer.brand.id)
  const { seller } = useVariantSeller(offer, variant)

  const soldAndDeliveredTooltipDescription = intl.formatMessage(
    {
      id: 'qboyjQHP6QxwSA9ov0iS',
      defaultMessage: '{sellerName} is a verified seller on Luxury Escapes Shop. All sellers are verified and fulfil orders directly from within Australia.',
      description: 'Tooltip description explaining verified seller on merchandise offer page',
    },
    { sellerName: seller.name },
  )

  const { optionTypeData, onOptionSelect } = useMerchandiseOptionData(offer, variant)
  const { onAddToCart, onBuyNow } = useMerchandiseCartActions(offer, variant, quantity)

  const colorOptions = useMerchandiseColorOptions(offer.variants, variant)
  const shouldCombineColorOptions = colorOptions.shouldCombine

  const handleOptionSelect = useCallback((position: number, optionValueName: string) => {
    const newVariant = onOptionSelect(position, optionValueName)
    if (newVariant) {
      onVariantChange?.(newVariant)
    }
  }, [onOptionSelect, onVariantChange])

  const onShowSizeChart = useCallback(() => {
    if (offer.sizing) {
      showModal(<Modal title={intl.formatMessage({
        id: 'eiB5VCuhbhjOwmpP9ium',
        defaultMessage: 'Size chart',
        description: 'Modal title for merchandise size chart in the merchandise shop section',
      })}
      >
        <MerchandiseOfferSizeChart sizeChart={offer.sizing} />
      </Modal>)
    }
  }, [offer.sizing, showModal, intl])

  const deliveryDateRange = useMemo(() => {
    const start = addBusinessDays(new Date(), seller.shipping.minDays)
    const end = addBusinessDays(new Date(), seller.shipping.maxDays)
    return formatDynamicDateRange(start, end)
  }, [seller])

  return <Details gap={40}>
    <VerticalSpacer gap={24}>
      <TextLink
        size="medium"
        colour="light-foreground-alt"
        weight="regular"
        underline={false}
        to={brand ? getMerchandiseBrandSearchUrl(brand) : getMerchandiseSearchUrl({ brands: [offer.brand.id] })}
        endIcon={<LineAngleRightIcon />}
      >
        {offer.brand.name}
      </TextLink>
      <VerticalSpacer gap={8}>
        <Heading variant="heading2">{offer.name}</Heading>
        <Tooltip placement="bottom-start" description={soldAndDeliveredTooltipDescription}>
          <BodyText variant="medium">
            <FormattedMessage
              id="wvAeJReOXs9qyFbba6pb"
              defaultMessage="<label>Sold and delivered by:</label> <b>{sellerName}</b>"
              description="Sold and delivered by label followed by seller name on merchandise offer page"
              values={{
                sellerName: seller.name,
                label: (chunks: React.ReactNode) => <Colour value="light-foreground-alt">{chunks}</Colour>,
                b: (chunks: React.ReactNode) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
        </Tooltip>
      </VerticalSpacer>
      <PriceRowMerchandisePriceStack
        direction="horizontal"
        size="L"
        variant={variant}
        quantity={quantity}
        displayEarnPoints
        discountBadgePosition="above"
      />
      {shouldCombineColorOptions && colorOptions.options.length > 0 && (
        <VerticalSpacer gap={8}>
          <VerticalSpacer gap={4}>
            {colorOptions.colorLabels.map(({ typeName, selectedName }) => (
              <BodyText key={typeName} variant="medium">
                <FormattedMessage
                  id="l-67nNaR7EVf39KAKerK"
                  defaultMessage="<label>{typeName}:</label> <b>{selectedName}</b>"
                  description="Merchandise option type label with colon for color selection"
                  values={{
                    typeName,
                    selectedName,
                    label: (chunks: React.ReactNode) => <Colour value="light-foreground-alt">{chunks}</Colour>,
                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  }}
                />
              </BodyText>
            ))}
          </VerticalSpacer>
          <Group direction="horizontal" gap={12} wrap="wrap">
            {colorOptions.options.map(option => (
              <MerchandiseColourOption
                key={option.key}
                interactive
                size={20}
                colors={option.colors}
                selected={option.isSelected}
                onClick={() => onVariantChange?.(option.variant!)}
              />
            ))}
          </Group>
        </VerticalSpacer>
      )}
      {optionTypeData.map(({ typeId, typeName, position, isColor, options, selectedOption }, index) => {
        if (!options.length || !selectedOption) {
          return null
        }

        // Skip first two color option types if we're combining them
        if (shouldCombineColorOptions && isColor && index < 2) {
          return null
        }

        const showSizeChart = typeName.toLowerCase() === 'size' && !!offer.sizing

        return (
          <VerticalSpacer key={typeId} gap={8}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium">
                <FormattedMessage
                  id="iv-kSBdZDJ44_cX7lorG"
                  defaultMessage="<label>{typeName}:</label> <b>{selectedName}</b>"
                  description="Merchandise option type label with colon for variant selection"
                  values={{
                    typeName,
                    selectedName: selectedOption.name,
                    label: (chunks: React.ReactNode) => <Colour value="light-foreground-alt">{chunks}</Colour>,
                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  }}
                />
              </BodyText>
              {showSizeChart && <TextLink size="medium" onClick={onShowSizeChart}>
                {intl.formatMessage({
                  id: 'COohwlY9mTfaMj0w2f2D',
                  defaultMessage: 'Size chart',
                  description: 'Hyperlink text to open size chart modal in the merchandise shop section',
                })}
              </TextLink>}
            </Group>
            <Group direction="horizontal" gap={12} wrap="wrap">
              {isColor && !shouldCombineColorOptions && options.map(option => {
                const colorOption = option as App.MerchandiseOfferColourOption
                return (
                  <MerchandiseColourOption
                    key={option.name}
                    interactive
                    size={20}
                    colors={[colorOption.colour]}
                    selected={option.name === selectedOption.name}
                    onClick={() => handleOptionSelect(position, option.name)}
                  />
                )
              })}
              {(!isColor || shouldCombineColorOptions) && options.map(option => (
                <ToggleButton
                  kind="secondary"
                  orientation="horizontal"
                  key={option.name}
                  isSelected={option.name === selectedOption.name}
                  onClick={() => handleOptionSelect(position, option.name)}
                  title={option.name}
                />
              ))}
            </Group>
          </VerticalSpacer>
        )
      })}
      <VerticalSpacer gap={8} data-testid="merch-quantity-section">
        <BodyText variant="medium" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'NE3eKTj14FPTu67S49vr',
            defaultMessage: 'Quantity:',
            description: 'Label for quantity selector on merchandise offer page',
          })}
        </BodyText>
        <Counter
          value={quantity}
          min={1}
          max={variant.inventory}
          onChange={onQuantityChange}
          disabled={!variant.inventory}
        />
        {quantity >= (variant.inventory ?? 0) && !!variant.inventory && (
          <MerchandiseMaxQuantityMessage align="start" />
        )}
      </VerticalSpacer>
      <MerchandiseLowStockMessage variants={[variant]} />
    </VerticalSpacer>

    <Group direction="horizontal" gap={8} horizontalAlign="stretch">
      <TextButton
        kind="secondary"
        startIcon={<LineShoppingCartIcon />}
        onClick={onAddToCart}
        disabled={!variant.inventory}
      >
        {intl.formatMessage({
          id: 'zgECFhf5CwzEjHN7H2Pn',
          defaultMessage: 'Add to cart',
          description: 'Button text to add merchandise item to shopping cart',
        })}
      </TextButton>
      <TextButton
        kind="primary"
        disabled={!variant.inventory}
        onClick={onBuyNow}
      >
        {intl.formatMessage({
          id: 'pRLRFPT-xNSmJPs4m69Y',
          defaultMessage: 'Buy now',
          description: 'Button text to purchase merchandise item immediately',
        })}
      </TextButton>
    </Group>
    <MerchandiseOfferPagePointsPayCalculator
      offer={offer}
      variant={variant}
      quantity={quantity}
    />
    <div>
      <VerticalSpacer gap={4}>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'QpzeR1JOO7JMLy3SngSa',
            defaultMessage: 'Delivery',
            description: 'Section heading for delivery information on merchandise offer page',
          })}
        </BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="AZC4ISLXp3m-ly4Dfx5M"
            defaultMessage="This item is sent directly from {sellerName} and will arrive <bold>separately</bold> if ordered with other items."
            description="Delivery information about item shipping separately"
            values={{
              sellerName: seller.name,
              bold: (chunks) => <b>{chunks}</b>,
            }}
          />
          <br /><br />
          <FormattedMessage
            id="4lxwxkfQUw6u1Iaa2CLZ"
            defaultMessage="Estimated delivery date: <bold>{dateRange}</bold>"
            description="Estimated delivery date range displayed for a merchandise product on the offer page"
            values={{
              dateRange: deliveryDateRange,
              bold: (chunks) => <b>{chunks}</b>,
            }}
          />
        </BodyText>
      </VerticalSpacer>
    </div>
  </Details>
}

export default MerchandiseOfferPageDetails
